// @givmo/app-admin — the match-policy onboarding wizard (07-1.1b).
//
// A 3-step parity rebuild of the Flutter `OnboardingScreen` + cubit. Reached post-auth when the
// signed-in company has no per-employee match budget (the trigger lives in the authenticated
// layout; see onboarding-route.tsx). The wizard collects:
//   - step 1 (REQUIRED): per-employee match budget (whole-dollar integer; Next gated on > 0);
//   - step 2 (optional):  an aggregate company cap (checkbox + money input);
//   - step 3 (optional):  a custom message (checkbox + textarea) + the read-only required-language.
//
// Finish (and Skip on the last step) submits. Money is sent as WHOLE-DOLLAR INTEGERS — this
// endpoint differs from the donation/grant decimal-string money. Unchecked optional sections are
// forced to `0` / `''` on the wire regardless of stale field state. A budget of null/≤0 is
// guarded client-side (verbatim message, no PUT). On success: invalidate the company query, set the
// per-company dismissal flag, and leave to the authenticated landing. "Save and finish later" sets
// the dismissal flag and leaves without submitting.
//
// State is local (no cubit/global store) — the wizard mounts once for a fresh admin and is
// short-lived. The api-client mutation hook + the company query key come from @givmo/api-client/query.

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  companiesUpdateCompanyMatchPolicyMutation,
  companiesGetCompanyQueryKey,
} from '@givmo/api-client/query';
import { isGivmoError, supportReference, type CompanyDetailedOut } from '@givmo/api-client';
import { Button, Card, CardBody, CardFooter, Icon, toast, testIdProp } from '@givmo/design-system';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import {
  ONBOARDING_STEP_COUNT,
  ONBOARDING_BUDGET_REQUIRED_MESSAGE,
  readYearBudgetPerEmployee,
} from './onboarding-consts';
import { buildMatchPolicyBody } from '../match-program/match-policy';
import { dismissOnboarding } from './onboarding-dismissal';
import { OnboardingStepOne } from './onboarding-step-one';
import { OnboardingStepTwo } from './onboarding-step-two';
import { OnboardingStepThree } from './onboarding-step-three';

export interface OnboardingWizardProps {
  /** The signed-in company (already loaded by the layout). Primes the form + supplies the id. */
  company: CompanyDetailedOut;
  /** Navigate to the authenticated landing (dashboard) — called on dismiss + on success. */
  onLeave: () => void;
}

interface WizardForm {
  budget: number | null;
  annualChecked: boolean;
  annualValue: number | null;
  customChecked: boolean;
  customValue: string;
}

/** Prime the form from any existing policy so a returning, mid-flow admin doesn't lose values. */
function primeForm(company: CompanyDetailedOut): WizardForm {
  // `match_policy` is the api-client's `MatchPolicyOut | {} | null` union — read the nested optional
  // sections tolerantly (the open-record `{}` fallback has no typed members).
  const policy = (company.match_policy ?? {}) as {
    annual_donation_limit?: { is_checked?: boolean; value?: number | null };
    custom_language?: { is_checked?: boolean; value?: string | null };
  };
  const annual = policy.annual_donation_limit;
  const custom = policy.custom_language;
  return {
    budget: readYearBudgetPerEmployee(company),
    annualChecked: annual?.is_checked ?? false,
    annualValue: typeof annual?.value === 'number' ? annual.value : null,
    customChecked: custom?.is_checked ?? false,
    customValue: typeof custom?.value === 'string' ? custom.value : '',
  };
}

export function OnboardingWizard({ company, onLeave }: OnboardingWizardProps) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<WizardForm>(() => primeForm(company));
  const [guardMessage, setGuardMessage] = useState<string | null>(null);
  // The support-quotable `ref: …` for a PERSISTENT submit failure (Flutter's ErrorReferenceText).
  // `supportReference` already suppresses transient/connection failures (the `_persistentRequestId`
  // predicate), so support can correlate a failed match-policy save without noise on retryable errors.
  const [errorReference, setErrorReference] = useState<string | null>(null);

  const isLastStep = step === ONBOARDING_STEP_COUNT - 1;
  // Step 1's Next is gated on a per-employee budget > 0; steps 2 & 3 always advance.
  const canAdvance = step !== 0 || (form.budget ?? 0) > 0;

  const updateMutation = useMutation({
    ...companiesUpdateCompanyMatchPolicyMutation(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: companiesGetCompanyQueryKey() });
      dismissOnboarding(company.id);
      toast.success('Match policy saved');
      onLeave();
    },
    onError: (error) => {
      setGuardMessage(
        isGivmoError(error) ? error.message : 'Something went wrong. Please try again.',
      );
      setErrorReference(isGivmoError(error) ? supportReference(error) : null);
    },
  });

  const submitting = updateMutation.isPending;

  const leaveForLater = () => {
    dismissOnboarding(company.id);
    onLeave();
  };

  const submit = () => {
    setGuardMessage(null);
    setErrorReference(null);
    // Client guard: a null/≤0 budget would not clear the dashboard's "completed" check — block the
    // PUT and surface the verbatim message (no HTTP fired, so no ref).
    if (form.budget === null || form.budget <= 0) {
      setGuardMessage(ONBOARDING_BUDGET_REQUIRED_MESSAGE);
      return;
    }
    // Force unchecked optional sections to 0 / '' regardless of stale field state. The body shape
    // is built by the SHARED `buildMatchPolicyBody` so the Match Program builder can't diverge.
    updateMutation.mutate({ body: buildMatchPolicyBody(form) });
  };

  const goNext = () => {
    if (isLastStep) {
      submit();
    } else {
      setStep((s) => Math.min(s + 1, ONBOARDING_STEP_COUNT - 1));
    }
  };

  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  return (
    <div
      className="relative flex min-h-screen flex-col bg-surface-sunken"
      {...testIdProp('onboarding-wizard')}
    >
      {/* Faint brand wash so the wizard sits in a considered canvas (not a blank void). */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-gradient-to-b from-brand-50/70 via-surface-sunken to-surface-sunken"
      />

      {/* Top-right "Save and finish later" link. */}
      <div className="relative z-10 flex justify-end px-24 pt-24">
        <Button
          appearance="saas"
          tone="link"
          size="md"
          onClick={leaveForLater}
          testId="onboarding-dismiss"
        >
          Save and finish later
        </Button>
      </div>

      {/* Vertically + horizontally centered wizard column. */}
      <div className="relative z-10 mx-auto flex w-full max-w-[720px] flex-1 flex-col justify-center px-24 py-16">
        {/* Step indicator + progress bar. */}
        <div className="flex items-center gap-16" {...testIdProp('onboarding-step-indicator')}>
          <span className="whitespace-nowrap font-sans text-s13 font-w600 tracking-normal text-brand-700">
            Step {step + 1} of {ONBOARDING_STEP_COUNT}
          </span>
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-neutral-200">
            <div
              className="h-full rounded-full bg-brand-600 transition-all"
              style={{ width: `${((step + 1) / ONBOARDING_STEP_COUNT) * 100}%` }}
            />
          </div>
        </div>

        <Card className="mt-20">
          <CardBody>
            {step === 0 && (
              <OnboardingStepOne
                budget={form.budget}
                onBudgetChange={(budget) => setForm((f) => ({ ...f, budget }))}
              />
            )}
            {step === 1 && (
              <OnboardingStepTwo
                isChecked={form.annualChecked}
                value={form.annualValue}
                onChange={({ isChecked, value }) =>
                  setForm((f) => ({ ...f, annualChecked: isChecked, annualValue: value }))
                }
              />
            )}
            {step === 2 && (
              <OnboardingStepThree
                isChecked={form.customChecked}
                value={form.customValue}
                onChange={({ isChecked, value }) =>
                  setForm((f) => ({ ...f, customChecked: isChecked, customValue: value }))
                }
              />
            )}

            {guardMessage !== null && (
              <div className="mt-24 flex flex-col gap-4">
                <p
                  className="font-sans text-s14 font-w500 tracking-normal text-danger-700"
                  {...testIdProp('onboarding-error')}
                >
                  {guardMessage}
                </p>
                {errorReference !== null && (
                  <p
                    className="font-sans text-s12 font-w400 tracking-normal text-text-tertiary"
                    {...testIdProp('onboarding-error-reference')}
                  >
                    {errorReference}
                  </p>
                )}
              </div>
            )}
          </CardBody>

          {/* Footer: Back (step > 0) · Skip for now (step > 0) · Next/Finish. */}
          <CardFooter className="justify-between">
            {step > 0 ? (
              <Button
                appearance="saas"
                tone="ghost"
                size="md"
                disabled={submitting}
                leadingIcon={<Icon icon={ArrowLeft} size={16} aria-hidden />}
                onClick={goBack}
                testId="onboarding-back"
              >
                Back
              </Button>
            ) : (
              <span />
            )}

            <div className="flex items-center gap-8">
              {step > 0 && (
                <Button
                  appearance="saas"
                  tone="ghost"
                  size="md"
                  disabled={submitting}
                  onClick={isLastStep ? submit : goNext}
                  testId="onboarding-skip"
                >
                  Skip for now
                </Button>
              )}
              <Button
                appearance="saas"
                tone="primary"
                size="md"
                disabled={!canAdvance || submitting}
                loading={submitting && isLastStep}
                trailingIcon={
                  isLastStep ? (
                    <Icon icon={Check} size={16} aria-hidden />
                  ) : (
                    <Icon icon={ArrowRight} size={16} aria-hidden />
                  )
                }
                onClick={goNext}
                testId={isLastStep ? 'onboarding-finish' : 'onboarding-next'}
              >
                {isLastStep ? 'Finish' : 'Next'}
              </Button>
            </div>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
